import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import CustomerSearchSelectAsync from '../../../../../components/CustomerSearchSelectAsync';
import { getClientContacts } from '../../../../../services/customer';
import { fetchClientContactOptions } from '../../../sales/orders/clientContactSelection';

const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
const auth = () => ({ headers: { accessToken: localStorage.getItem('accessToken') } });

export default function ProjectCloneDialog({ open, onClose, instanceUniqueId }) {
  const [query, setQuery] = useState('');
  const [sources, setSources] = useState([]);
  const [source, setSource] = useState(null);
  const [preview, setPreview] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);
  const searchSeq = useRef(0);

  useEffect(() => {
    if (!open) {
      setQuery(''); setSources([]); setSource(null); setPreview(null);
      setCustomerId(null); setContacts([]); setContactId(''); setName('');
      setBusy(false); setError(''); setCreated(null); searchSeq.current++;
    }
  }, [open]);

  useEffect(() => {
    if (!open || query.trim().length < 2) { setSources([]); return; }
    const seq = ++searchSeq.current;
    let active = true;
    const timeout = setTimeout(async () => {
      try {
        const response = await axios.get(
          SERVER + '/api/instances/' + encodeURIComponent(instanceUniqueId) + '/tools/project-clone/search',
          { ...auth(), params: { q: query.trim() } }
        );
        if (active && seq === searchSeq.current) setSources(response.data?.results || []);
      } catch (_) {
        if (active && seq === searchSeq.current) setSources([]);
      }
    }, 250);
    return () => { active = false; clearTimeout(timeout); };
  }, [open, instanceUniqueId, query]);

  useEffect(() => {
    if (!open || !source) { setPreview(null); return; }
    let active = true;
    setPreview(null);
    axios.get(SERVER + '/api/instances/' + encodeURIComponent(instanceUniqueId) +
      '/tools/project-clone/preview/' + source.id, auth())
      .then(({ data }) => { if (active) setPreview(data); })
      .catch(err => { if (active) setError(err.response?.data?.message || 'Could not preview project.'); });
    return () => { active = false; };
  }, [open, instanceUniqueId, source]);

  useEffect(() => {
    let active = true;
    setContactId('');
    setContacts([]);
    if (open && customerId) {
      fetchClientContactOptions(customerId, getClientContacts)
        .then(rows => { if (active) setContacts(rows); })
        .catch(() => { if (active) setError('Could not load contacts for the customer.'); });
    }
    return () => { active = false; };
  }, [open, customerId]);

  const submit = async () => {
    setError(''); setBusy(true);
    try {
      const response = await axios.post(
        SERVER + '/api/instances/' + encodeURIComponent(instanceUniqueId) + '/tools/project-clone',
        { sourceProjectId: source.id, customerId, contactId, projectName: name.trim() },
        auth()
      );
      setCreated(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to clone project.');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Clone Project <Typography component="span" color="text.secondary" variant="caption"> · Administrator test tool</Typography></DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="warning">TEST ONLY. The clone starts as a new draft. Do not send documents, submit purchase orders, or create invoices from test projects.</Alert>
          {!created ? (
            <>
              <Autocomplete
                options={sources} value={source} filterOptions={x => x}
                isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
                getOptionLabel={option => option ? ('#' + option.projectNumber + ' · ' + option.name + ' (' + option.customer + ')') : ''}
                onChange={(_, value) => {
                  setSource(value); setName(value ? 'TEST - ' + value.name : '');
                  setError('');
                }}
                renderInput={params => <TextField {...params} label="Source project" placeholder="Search project number or name"
                  onChange={e => setQuery(e.target.value)} />}
              />
              {preview && <Alert severity="info">
                Copy {preview.counts.products} products, {preview.counts.pricingRows} pricing rows, {preview.counts.decorations} decorations, {preview.counts.services} services and {preview.counts.sections} sections.
                Original project and its financial/document history will remain unchanged.
              </Alert>}
              <TextField fullWidth label="New project name" value={name} onChange={e => setName(e.target.value)} />
              <CustomerSearchSelectAsync
                instanceUniqueId={instanceUniqueId} value={customerId || ''}
                label="New customer account" onChange={value => { setCustomerId(value); setError(''); }}
              />
              <TextField select fullWidth label="New customer contact" value={contactId}
                onChange={e => setContactId(e.target.value)}
                SelectProps={{ native: true }} disabled={!customerId}>
                <option value="">Select a contact</option>
                {contacts.map(contact => (
                  <option key={contact.contactId} value={contact.contactId}>
                    {contact.displayLabel}
                  </option>
                ))}
              </TextField>
              <Typography color="text.secondary" variant="body2">
                The signed-in administrator is assigned automatically. Pricing and supplier details are copied; all order-item and document identities are new.
              </Typography>
            </>
          ) : (
            <Alert severity="success">
              Test project #{created.projectNumber} created with {created.counts.products} products.
              {' '}<a href={'/instance/' + instanceUniqueId + '/sales/orders/' + created.projectNumber + '/overview'}>Open new project</a>
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{created ? 'Close' : 'Cancel'}</Button>
        {!created && <Button variant="contained" onClick={submit}
          disabled={busy || !preview || !customerId || !contactId || name.trim().length < 3}>
          {busy ? <CircularProgress size={18} /> : 'Create Test Clone'}
        </Button>}
      </DialogActions>
    </Dialog>
  );
}
